import * as THREE from '@phdsoft/three';
import { Util } from '@phdsoft/common';

export class RepairGeometryUtils {

    public static screenToLocal(
        camera: THREE.Camera,
        screenX: number,
        screenY: number,
        localZ: number
    ): THREE.Vector3 {
        let ndcX = (screenX / window.innerWidth) * 2 - 1;
        let ndcY = (-screenY / window.innerHeight) * 2 + 1;
        let rayc = new THREE.Raycaster();
        rayc.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);
        let newpos = new THREE.Vector3();
        rayc.ray.at(localZ, newpos);
        camera.worldToLocal(newpos);
        return newpos;
    }

    public static generateCircleVertices(
        camera: THREE.Camera,
        centerX: number,
        centerY: number,
        radius: number,
        numSegments: number,
        distz: number
    ): THREE.Vector3[] {
        const vertices: THREE.Vector3[] = [];

        // Add center point first (for fan triangulation)
        vertices.push(RepairGeometryUtils.screenToLocal(camera, centerX, centerY, distz));

        // Generate circle vertices
        for (let i = 0; i <= numSegments; i++) {
            const angle = (i / numSegments) * Math.PI * 2;
            const x = centerX + radius * Math.cos(angle);
            const y = centerY + radius * Math.sin(angle);
            vertices.push(RepairGeometryUtils.screenToLocal(camera, x, y, distz));
        }

        return vertices;
    }

    public static generateEllipseVertices(
        camera: THREE.Camera,
        centerX: number,
        centerY: number,
        radiusX: number,
        radiusY: number,
        numSegments: number,
        distz: number
    ): THREE.Vector3[] {
        const vertices: THREE.Vector3[] = [];

        // Add center point first (for fan triangulation)
        vertices.push(RepairGeometryUtils.screenToLocal(camera, centerX, centerY, distz));

        // Generate ellipse vertices
        for (let i = 0; i <= numSegments; i++) {
            const angle = (i / numSegments) * Math.PI * 2;
            const x = centerX + radiusX * Math.cos(angle);
            const y = centerY + radiusY * Math.sin(angle);
            vertices.push(RepairGeometryUtils.screenToLocal(camera, x, y, distz));
        }

        return vertices;
    }

    public static isPointInsideSphere(point: THREE.Vector3, center: THREE.Vector3, radius: number): boolean {
        if (!center || radius === null || radius === undefined || radius <= 0) {
            return false;
        }
        const distance = point.distanceTo(center);
        // Use tolerance to account for floating point errors and coordinate space differences
        const isInside = distance <= radius + 0.1;
        return isInside;
    }

    public static isPointInsideCircle(point: THREE.Vector3, center: THREE.Vector3, radius: number): boolean {
        return RepairGeometryUtils.isPointInsideSphere(point, center, radius);
    }

    public static isPointInsideCircleScreenSpace(
        camera: THREE.Camera,
        pointWorld: THREE.Vector3,
        centerX: number,
        centerY: number,
        radius: number
    ): boolean {
        // Project the 3D point to screen space
        const pointScreen = pointWorld.clone().project(camera);

        // Check if point is behind camera (z > 1 in NDC means behind camera)
        if (pointScreen.z > 1) {
            return false;
        }

        // Convert from NDC (-1 to 1) to screen coordinates
        const screenX = (pointScreen.x * 0.5 + 0.5) * window.innerWidth;
        const screenY = (-pointScreen.y * 0.5 + 0.5) * window.innerHeight;

        // Calculate distance from circle center in screen space
        const dx = screenX - centerX;
        const dy = screenY - centerY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // Check if inside circle with adaptive tolerance
        // Check if inside circle with strict limit (no tolerance) to avoid spikes on curved surfaces
        return distance <= radius;
    }

    public static findLineCircleIntersectionsScreenSpace(
        camera: THREE.Camera,
        startWorld: THREE.Vector3,
        endWorld: THREE.Vector3,
        centerX: number,
        centerY: number,
        radius: number
    ): THREE.Vector3[] {
        const intersections: THREE.Vector3[] = [];

        const startScreen = startWorld.clone().project(camera);
        const endScreen = endWorld.clone().project(camera);

        if (startScreen.z > 1 && endScreen.z > 1) {
            return intersections;
        }

        const startX = (startScreen.x * 0.5 + 0.5) * window.innerWidth;
        const startY = (-startScreen.y * 0.5 + 0.5) * window.innerHeight;
        const endX = (endScreen.x * 0.5 + 0.5) * window.innerWidth;
        const endY = (-endScreen.y * 0.5 + 0.5) * window.innerHeight;

        const dx = endX - startX;
        const dy = endY - startY;
        const dLength = Math.sqrt(dx * dx + dy * dy);

        if (dLength < 1e-9) {
            return intersections;
        }

        const fx = startX - centerX;
        const fy = startY - centerY;

        // Solve quadratic equation for line-circle intersection in 2D screen space
        const a = dx * dx + dy * dy;
        const b = 2 * (fx * dx + fy * dy);
        const c = fx * fx + fy * fy - radius * radius;

        const discriminant = b * b - 4 * a * c;

        if (discriminant < 0) {
            return intersections;
        }

        const sqrtDiscriminant = Math.sqrt(discriminant);
        const t1 = (-b - sqrtDiscriminant) / (2 * a);
        const t2 = (-b + sqrtDiscriminant) / (2 * a);

        // The quadratic above gives t in screen space. Directly lerping world-space positions
        // with a screen-space t is only correct when the camera is perpendicular to the surface.
        // Under oblique views, perspective foreshortening makes screen-space t non-linear in
        // world space, displacing the intersection point and breaking the angular sort in
        // createPartialTriangles(). We correct this using the clip-space w values of both
        // endpoints: t_world = (t_screen * w0) / (t_screen * w0 + (1 - t_screen) * w1)
        const mvp = new THREE.Matrix4().multiplyMatrices(
            camera.projectionMatrix,
            (camera as any).matrixWorldInverse
        );
        const start4 = new THREE.Vector4(startWorld.x, startWorld.y, startWorld.z, 1).applyMatrix4(mvp);
        const end4   = new THREE.Vector4(endWorld.x,   endWorld.y,   endWorld.z,   1).applyMatrix4(mvp);
        const w0 = start4.w;
        const w1 = end4.w;

        const perspCorrectT = (tScreen: number): number => {
            const denom = tScreen * w0 + (1 - tScreen) * w1;
            if (Math.abs(denom) < 1e-9) return tScreen;
            return (tScreen * w0) / denom;
        };

        const tolerance = 0.001;

        if (t1 >= -tolerance && t1 <= 1 + tolerance) {
            const tWorld = perspCorrectT(Math.max(0, Math.min(1, t1)));
            const intersectionWorld = startWorld.clone().lerp(endWorld, tWorld);
            intersections.push(intersectionWorld);
        }

        if (Math.abs(t1 - t2) > tolerance && t2 >= -tolerance && t2 <= 1 + tolerance) {
            const tWorld = perspCorrectT(Math.max(0, Math.min(1, t2)));
            const intersectionWorld = startWorld.clone().lerp(endWorld, tWorld);
            // Avoid duplicates - use smaller tolerance for 3D distance check
            if (intersections.length === 0 || intersections.every(int => int.distanceTo(intersectionWorld) > 0.001)) {
                intersections.push(intersectionWorld);
            }
        }

        return intersections;
    }

    public static isPointInsideEllipseScreenSpace(
        camera: THREE.Camera,
        pointWorld: THREE.Vector3,
        centerX: number,
        centerY: number,
        radiusX: number,
        radiusY: number
    ): boolean {
        // Project the 3D point to screen space
        const pointScreen = pointWorld.clone().project(camera);

        // Check if point is behind camera (z > 1 in NDC means behind camera)
        if (pointScreen.z > 1) {
            return false;
        }

        // Convert from NDC (-1 to 1) to screen coordinates
        const screenX = (pointScreen.x * 0.5 + 0.5) * window.innerWidth;
        const screenY = (-pointScreen.y * 0.5 + 0.5) * window.innerHeight;

        // Ellipse equation: (x-cx)^2/rx^2 + (y-cy)^2/ry^2 <= 1
        const dx = screenX - centerX;
        const dy = screenY - centerY;

        // Use normalized distance equation
        const normalizedDist = (dx * dx) / (radiusX * radiusX) + (dy * dy) / (radiusY * radiusY);

        // Strict check without adaptive tolerance for curved surfaces
        return normalizedDist <= 1.0;
    }

    public static findLineEllipseIntersectionsScreenSpace(
        camera: THREE.Camera,
        startWorld: THREE.Vector3,
        endWorld: THREE.Vector3,
        centerX: number,
        centerY: number,
        radiusX: number,
        radiusY: number
    ): THREE.Vector3[] {
        const intersections: THREE.Vector3[] = [];

        const startScreen = startWorld.clone().project(camera);
        const endScreen = endWorld.clone().project(camera);

        if (startScreen.z > 1 && endScreen.z > 1) {
            return intersections;
        }

        const startX = (startScreen.x * 0.5 + 0.5) * window.innerWidth;
        const startY = (-startScreen.y * 0.5 + 0.5) * window.innerHeight;
        const endX = (endScreen.x * 0.5 + 0.5) * window.innerWidth;
        const endY = (-endScreen.y * 0.5 + 0.5) * window.innerHeight;

        const dx = endX - startX;
        const dy = endY - startY;
        const dLength = Math.sqrt(dx * dx + dy * dy);

        if (dLength < 1e-9) {
            return intersections;
        }

        const fx = startX - centerX;
        const fy = startY - centerY;

        const rx2 = radiusX * radiusX;
        const ry2 = radiusY * radiusY;

        // Quadratic equation coefficients A*t^2 + B*t + C = 0
        const A = ry2 * dx * dx + rx2 * dy * dy;
        const B = 2 * (ry2 * fx * dx + rx2 * fy * dy);
        const C = ry2 * fx * fx + rx2 * fy * fy - rx2 * ry2;

        const discriminant = B * B - 4 * A * C;

        if (discriminant < 0) {
            return intersections;
        }

        const sqrtDiscriminant = Math.sqrt(discriminant);
        const t1 = (-B - sqrtDiscriminant) / (2 * A);
        const t2 = (-B + sqrtDiscriminant) / (2 * A);

        const tolerance = 0.001;
        if (t1 >= -tolerance && t1 <= 1 + tolerance) {
            const intersectionWorld = startWorld.clone().lerp(endWorld, Math.max(0, Math.min(1, t1)));
            intersections.push(intersectionWorld);
        }

        if (Math.abs(t1 - t2) > tolerance && t2 >= -tolerance && t2 <= 1 + tolerance) {
            const intersectionWorld = startWorld.clone().lerp(endWorld, Math.max(0, Math.min(1, t2)));
            // Avoid duplicates - use smaller tolerance for 3D distance check
            if (intersections.length === 0 || intersections.every(int => int.distanceTo(intersectionWorld) > 0.001)) {
                intersections.push(intersectionWorld);
            }
        }

        return intersections;
    }

    public static vertIntersectPlanes(p1: THREE.Plane, p2: THREE.Plane, p3: THREE.Plane): THREE.Vector3 {
        let n1 = p1.normal,
            n2 = p2.normal,
            n3 = p3.normal;
        let x1 = p1.coplanarPoint(new THREE.Vector3());
        let x2 = p2.coplanarPoint(new THREE.Vector3());
        let x3 = p3.coplanarPoint(new THREE.Vector3());
        let f1 = n2.clone().cross(n3).multiplyScalar(x1.dot(n1));
        let f2 = n3.clone().cross(n1).multiplyScalar(x2.dot(n2));
        let f3 = n1.clone().cross(n2).multiplyScalar(x3.dot(n3));
        let det = new THREE.Matrix3()
            .set(n1.x, n1.y, n1.z, n2.x, n2.y, n2.z, n3.x, n3.y, n3.z)
            .determinant();
        let vectorSum = new THREE.Vector3().add(f1).add(f2).add(f3);
        let planeIntersection = new THREE.Vector3(
            vectorSum.x / det,
            vectorSum.y / det,
            vectorSum.z / det,
        );
        return planeIntersection;
    }

    public static vertIntersectLines3D(la: THREE.Line3, lb: THREE.Line3, lc: THREE.Line3, mua?: number, mub?: number): boolean {
        const EPS = 1e-9;
        let [p1, p2] = [la.start, la.end];
        let [p3, p4] = [lb.start, lb.end];

        let p43 = { x: p4.x - p3.x, y: p4.y - p3.y, z: p4.z - p3.z };
        if (Math.abs(p43.x) < EPS && Math.abs(p43.y) < EPS && Math.abs(p43.z) < EPS) return false;

        let p21 = { x: p2.x - p1.x, y: p2.y - p1.y, z: p2.z - p1.z };
        if (Math.abs(p21.x) < EPS && Math.abs(p21.y) < EPS && Math.abs(p21.z) < EPS) return false;

        let p13 = { x: p1.x - p3.x, y: p1.y - p3.y, z: p1.z - p3.z };

        let d1343 = p13.x * p43.x + p13.y * p43.y + p13.z * p43.z;
        let d4321 = p43.x * p21.x + p43.y * p21.y + p43.z * p21.z;
        let d1321 = p13.x * p21.x + p13.y * p21.y + p13.z * p21.z;
        let d4343 = p43.x * p43.x + p43.y * p43.y + p43.z * p43.z;
        let d2121 = p21.x * p21.x + p21.y * p21.y + p21.z * p21.z;

        let denom = d2121 * d4343 - d4321 * d4321;

        if (Math.abs(denom) < EPS) return false;

        let numer = d1343 * d4321 - d1321 * d4343;

        mua = numer / denom;
        mub = (d1343 + d4321 * mua) / d4343;

        let pa = new THREE.Vector3(p1.x + mua * p21.x, p1.y + mua * p21.y, p1.z + mua * p21.z);
        let pb = new THREE.Vector3(p3.x + mub * p43.x, p3.y + mub * p43.y, p3.z + mub * p43.z);

        if (lc) lc.set(pa, pb);

        let distLa = la.closestPointToPointParameter(pa);
        let distLb = lb.closestPointToPointParameter(pb);
        let linesIntersect = distLa >= 0 && distLa <= 1 && distLb >= 0 && distLb <= 1;
        return linesIntersect;
    }

    public static createPartialTriangles(
        camera: THREE.Camera,
        preVerts: THREE.Vector3[],
        plateNormal: THREE.Vector3,
        sideSign: number,
        shapeType: 'rectangle' | 'circle' | 'ellipse',
        triDebug?: any,
        debugCallback?: (verts: THREE.Vector3[]) => void
    ): THREE.Vector3[] {
        if (Util.isNullOrUndefined(preVerts) || preVerts.length < 3) {
            console.log(
                ">>> WARNING: this part of the code shouldn't be reached. ['createPartialTriangles()' with less than 3 vertices]",
            );
            return [];
        }

        if (debugCallback && triDebug) {
            debugCallback(triDebug.verts);
        }

        let cleanedVerts: THREE.Vector3[] = [];
        const minDistance = (shapeType === 'circle' || shapeType === 'ellipse') ? 0.000001 : 0.0001;
        for (const vert of preVerts) {
            const isDuplicate = cleanedVerts.some(existing => existing.distanceTo(vert) < minDistance);
            if (!isDuplicate) {
                cleanedVerts.push(vert);
            }
        }

        if (cleanedVerts.length < 3) {
            cleanedVerts = preVerts;
        } else {
            preVerts = cleanedVerts;
        }

        let postVerts: THREE.Vector3[];
        {
            const isCircleOrEllipse = shapeType === 'circle' || shapeType === 'ellipse';
            if (isCircleOrEllipse) {
                // Robust Plane-Based Centroid Sort
                // 1. Calculate Centroid
                const centroid = new THREE.Vector3();
                preVerts.forEach(v => centroid.add(v));
                centroid.divideScalar(preVerts.length);

                // 2. Define Plane Basis (u, v)
                let normal = plateNormal.clone().normalize();
                if (triDebug) {
                    normal = triDebug.getNormal(new THREE.Vector3()).normalize();
                }

                // Choose an arbitrary vector different from normal to generate 'u'
                // If normal is close to Y up, use Z. Otherwise use Y.
                const arbitrary = Math.abs(normal.y) > 0.9 ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(0, 1, 0);

                const u = new THREE.Vector3().crossVectors(arbitrary, normal).normalize();
                const v = new THREE.Vector3().crossVectors(normal, u).normalize();

                // 3. Sort by Angle in 2D Plane
                preVerts.sort((vecA, vecB) => {
                    const da = vecA.clone().sub(centroid);
                    const db = vecB.clone().sub(centroid);
                    const angleA = Math.atan2(da.dot(v), da.dot(u));
                    const angleB = Math.atan2(db.dot(v), db.dot(u));
                    return angleA - angleB;
                });

                postVerts = [...preVerts];
            } else {
                let firstVert = preVerts[0].clone();
                let funcDistFromFirstVec = (x) => x.clone().sub(firstVert);
                let funcNormVec = (x) => funcDistFromFirstVec(x).normalize();

                let listDistFromFirstVert = [...preVerts];
                listDistFromFirstVert.sort(
                    (a, b) => funcDistFromFirstVec(a).length() - funcDistFromFirstVec(b).length(),
                );
                let closest = listDistFromFirstVert[1];

                postVerts = preVerts.slice(1);
                postVerts.splice(
                    postVerts.findIndex((x) => x == closest),
                    1,
                );
                let firstVector = funcNormVec(closest);
                postVerts.sort((a, b) => funcNormVec(b).dot(firstVector) - funcNormVec(a).dot(firstVector));
                postVerts = [firstVert, closest, ...postVerts];
            }
        }

        const isCircleOrEllipse = shapeType === 'circle' || shapeType === 'ellipse';
        if (!isCircleOrEllipse && postVerts.length >= 4) {
            let reducedPoints: [THREE.Vector3, THREE.Vector3][] = [];
            let p0: THREE.Vector3 = postVerts[0].clone();

            for (var i = 0; i < postVerts.length; i++) {
                if (i == 0) continue;

                var point: THREE.Vector3 = postVerts[i];

                var reducedPoint = point.clone().add(p0.clone().negate());
                reducedPoints.push([reducedPoint, point]);
            }

            var angles: [number, number, number][] = [];
            var greaterAngle = [0, 0, 0];

            for (var i = 0; i < reducedPoints.length; i++) {
                var pointA: THREE.Vector3 = reducedPoints[i][0];
                for (var j = i + 1; j < reducedPoints.length; j++) {
                    if (i != j) {
                        var pointB: THREE.Vector3 = reducedPoints[j][0];
                        let angle = pointA.angleTo(pointB);
                        if (angle > greaterAngle[0]) {
                            greaterAngle = [angle, i, j];
                        }
                        angles.push([angle, i, j]);
                    }
                }
            }
            var secondPointIndex: number = greaterAngle[1];
            var filteredAngles = [];
            for (let k = 0; k < angles.length; k++) {
                let angle: [number, number, number] = angles[k];
                if (angle[1] == secondPointIndex || angle[2] == secondPointIndex) {
                    filteredAngles.push(angle);
                }
            }
            filteredAngles = filteredAngles.sort(a => a[0]);
            var newPointsList: THREE.Vector3[] = [];

            newPointsList.push(p0.clone());
            newPointsList.push(reducedPoints[secondPointIndex][1].clone());

            for (let l = 0; l < filteredAngles.length; l++) {
                let angle = filteredAngles[l];

                if (angle[1] == secondPointIndex) {
                    newPointsList.push(reducedPoints[angle[2]][1].clone());
                }
                else {
                    newPointsList.push(reducedPoints[angle[1]][1].clone());
                }
            }
            postVerts = newPointsList;
        }

        let retVerts = [];
        let p0 = postVerts[0];
        let p1;
        let p2;

        let refNormal = plateNormal;
        if (triDebug) {
            refNormal = triDebug.getNormal(new THREE.Vector3());
        }

        if (isCircleOrEllipse) {
            // Centroid Fan Triangulation
            // Using centroid as the central pivot creates better shaped triangles and avoids artifacts 
            // from long thin triangles in a perimeter fan.

            // Re-calculate centroid just to be sure we have the exact center of the sorted points
            const centroid = new THREE.Vector3();
            postVerts.forEach(v => centroid.add(v));
            centroid.divideScalar(postVerts.length);

            // Ref normal for winding check
            let refNormal = plateNormal;
            if (triDebug) {
                refNormal = triDebug.getNormal(new THREE.Vector3());
            }

            for (let i = 0; i < postVerts.length; i++) {
                p1 = postVerts[i];
                p2 = postVerts[(i + 1) % postVerts.length]; // Wrap around to close the loop

                // Check for degenerate triangle (zero area)
                if (p1.distanceTo(p2) < 0.000001) continue;

                let triNormal = p1.clone().sub(centroid).cross(p2.clone().sub(centroid)).normalize();

                // Ensure consistent winding order
                let v1 = p1, v2 = p2;

                if (sideSign !== 0) {
                    if (triNormal.dot(refNormal) * sideSign < 0)
                        [v1, v2] = [p2, p1];
                } else {
                    if (triNormal.dot(refNormal) < 0)
                        [v1, v2] = [p2, p1];
                }

                retVerts.push(centroid.clone(), v1, v2);
            }
        } else {
            // Standard Fan from p0 (Corner Fan) for other shapes (or if circle logic disabled)
            let retVertsCorner = [];
            let p0 = postVerts[0];
            let p1;
            let p2;

            let refNormal = plateNormal;
            if (triDebug) {
                refNormal = triDebug.getNormal(new THREE.Vector3());
            }

            for (let i = 1; i < postVerts.length - 1; i++) {
                p1 = postVerts[i];
                p2 = postVerts[i + 1];

                let triNormal = p1.clone().sub(p0).cross(p2.clone().sub(p0)).normalize();

                if (sideSign !== 0) {
                    if (triNormal.dot(refNormal) * sideSign < 0)
                        [p1, p2] = [p2, p1];
                } else {
                    if (triNormal.dot(refNormal) < 0)
                        [p1, p2] = [p2, p1];
                }

                retVerts.push(p0, p1, p2);
            }
            return retVerts;
        }

        if (debugCallback) {
            debugCallback(retVerts);
        }

        return retVerts;

        if (debugCallback) {
            debugCallback(retVerts);
        }

        return retVerts;
    }

    public static findLineCircleIntersections(line: THREE.Line3, center: THREE.Vector3, radius: number): THREE.Vector3[] {
        return RepairGeometryUtils.findLineSphereIntersections(line, center, radius);
    }

    public static findLineSphereIntersections(line: THREE.Line3, center: THREE.Vector3, radius: number): THREE.Vector3[] {
        const intersections: THREE.Vector3[] = [];
        const p1 = line.start;
        const p2 = line.end;

        // Vector from p1 to p2
        const d = p2.clone().sub(p1);
        const dLength = d.length();

        if (dLength < 1e-9) {
            // Degenerate line segment
            return intersections;
        }

        // Vector from center to p1
        const f = p1.clone().sub(center);

        // Solve quadratic equation: a*t^2 + b*t + c = 0
        const a = d.dot(d);
        const b = 2 * f.dot(d);
        const c = f.dot(f) - radius * radius;

        const discriminant = b * b - 4 * a * c;

        if (discriminant < 0) {
            // No intersection
            return intersections;
        }

        const sqrtDiscriminant = Math.sqrt(discriminant);
        const t1 = (-b - sqrtDiscriminant) / (2 * a);
        const t2 = (-b + sqrtDiscriminant) / (2 * a);

        // Check if intersection points are on the line segment (0 <= t <= 1)
        const tolerance = 0.001;
        if (t1 >= -tolerance && t1 <= 1 + tolerance) {
            const intersection = p1.clone().add(d.clone().multiplyScalar(Math.max(0, Math.min(1, t1))));
            intersections.push(intersection);
        }

        if (Math.abs(t1 - t2) > tolerance && t2 >= -tolerance && t2 <= 1 + tolerance) {
            const intersection = p1.clone().add(d.clone().multiplyScalar(Math.max(0, Math.min(1, t2))));
            // Avoid duplicate points
            if (intersections.length === 0 || intersection.distanceTo(intersections[0]) > tolerance) {
                intersections.push(intersection);
            }
        }

        return intersections;
    }

    public static isPointInsideRectangleScreenSpace(
        camera: THREE.Camera,
        pointWorld: THREE.Vector3,
        rectCorners: THREE.Vector3[]
    ): boolean {
        if (rectCorners.length !== 4) return false;

        // Project point to screen space
        const pointScreen = pointWorld.clone().project(camera);

        // Check if point is behind camera
        if (pointScreen.z > 1) {
            return false;
        }

        // Convert from NDC to screen coordinates
        const screenX = (pointScreen.x * 0.5 + 0.5) * window.innerWidth;
        const screenY = (-pointScreen.y * 0.5 + 0.5) * window.innerHeight;

        // Project rectangle corners to screen space
        const screenCorners = rectCorners.map(corner => {
            const projected = corner.clone().project(camera);
            return {
                x: (projected.x * 0.5 + 0.5) * window.innerWidth,
                y: (-projected.y * 0.5 + 0.5) * window.innerHeight
            };
        });

        // Use ray casting algorithm for point in polygon
        let inside = false;
        for (let i = 0, j = screenCorners.length - 1; i < screenCorners.length; j = i++) {
            const xi = screenCorners[i].x, yi = screenCorners[i].y;
            const xj = screenCorners[j].x, yj = screenCorners[j].y;

            const intersect = ((yi > screenY) !== (yj > screenY))
                && (screenX < (xj - xi) * (screenY - yi) / (yj - yi) + xi);

            if (intersect) inside = !inside;
        }

        return inside;
    }
}
